import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  FloatingPortal,
} from '@floating-ui/react';
import { useOptionalI18n } from '@/i18n';
import { localizeText } from '@/i18n/options';

interface YearSelectorProps {
  value: number;
  onChange: (year: number) => void;
  min?: number;
  max?: number;
  label?: React.ReactNode;
}

const YearSelector: React.FC<YearSelectorProps> = ({
  value,
  onChange,
  min = 1970,
  max = new Date().getFullYear(),
  label,
}) => {
  const { locale } = useOptionalI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [decadeStart, setDecadeStart] = useState(
    Math.floor((value || max) / 10) * 10
  );
  const referenceRef = useRef<HTMLButtonElement>(null);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
    ],
  });

  const handlePrevDecade = () => {
    setDecadeStart(Math.max(decadeStart - 10, Math.floor(min / 10) * 10));
  };

  const handleNextDecade = () => {
    setDecadeStart(Math.min(decadeStart + 10, Math.floor(max / 10) * 10));
  };

  const handleYearClick = (year: number) => {
    onChange(year);
    setIsOpen(false);
  };

  const setReferenceNode = (node: HTMLButtonElement | null) => {
    referenceRef.current = node;
    refs.setReference(node);
  };

  // Handle click outside to close picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const referenceNode = referenceRef.current;
      const floatingNode = refs.floating.current;

      if (referenceNode?.contains(target) || floatingNode?.contains(target)) return;
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, refs.floating]);

  const decadeEnd = decadeStart + 9;
  const years: number[] = [];
  for (let year = decadeStart; year <= decadeEnd && year <= max; year++) {
    if (year >= min) {
      years.push(year);
    }
  }

  const currentYear = new Date().getFullYear();

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      <button
        ref={setReferenceNode}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left font-semibold text-gray-900 hover:bg-gray-50 flex items-center justify-between transition-colors"
      >
        <span>{value}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <FloatingPortal>
        {isOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-dropdown"
          >
            {/* Decade Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={handlePrevDecade}
                disabled={decadeStart <= Math.floor(min / 10) * 10}
                className="p-1 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
                aria-label={localizeText(locale, 'Previous decade')}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <span className="font-semibold text-gray-900 text-center flex-1">
                {decadeStart} – {decadeEnd}
              </span>

              <button
                type="button"
                onClick={handleNextDecade}
                disabled={decadeStart >= Math.floor(max / 10) * 10}
                className="p-1 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
                aria-label={localizeText(locale, 'Next decade')}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Year Grid (4 columns) */}
            <div className="grid grid-cols-4 gap-2">
              {years.map((year) => (
                <button
                  key={year}
                  type="button"
                  onClick={() => handleYearClick(year)}
                  className={`py-2 px-1 rounded text-sm font-medium transition-colors ${
                    year === value
                      ? 'bg-accent text-white'
                      : year === currentYear
                      ? 'border border-accent text-accent hover:bg-accent hover:bg-opacity-10'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>

            {/* Range Info */}
            <p className="text-xs text-gray-500 mt-4 text-center">
              {min} – {max}
            </p>
          </div>
        )}
      </FloatingPortal>
    </div>
  );
};

export default YearSelector;
